import Section from '@/components/common/Section';
import Card from '@/components/cards/Card';
import USPCard from '@/components/cards/USPCard';
import ChatbotModal from '@/components/chatbot/ChatbotModal';
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/animations/variants';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const deliveryModel = [
  {
    title: "Residential Engineering Teams",
    description: "24×7 availability with high-speed execution model for consistent delivery and rapid response to changing requirements.",
    image: "/images/usp-residential-teams.png",
  },
  {
    title: "Consultant Network",
    description: "1000+ experts for architecture and stabilization across diverse technologies and industry domains.",
    image: "/images/usp-consultant-network.png",
  },
  {
    title: "Augmented Workforce",
    description: "Human expertise enhanced with GitHub Copilot and AI tools for maximum productivity and code quality.",
    image: "/images/usp-augmented-workforce.png",
  },
  {
    title: "Rapid Scaling Model",
    description: "Scale from 10 to 100+ resources rapidly while maintaining quality standards and delivery timelines.",
    image: "/images/usp-rapid-scale-model.png",
  },
];

const whyChooseUs = [
  {
    title: "Partner-Led Delivery",
    description: "Direct access to senior architects and technical leads throughout the project lifecycle.",
    icon: <img src="/icons/Hand-in-hand cooperation.gif" alt="Partner-Led Delivery" className="w-50 h-50 object-contain" />,
  },
  {
    title: "Clarity Over Process",
    description: "Transparent communication and clear deliverables without bureaucratic overhead.",
    icon: <img src="/icons/Clarity%20Over%20Process.png" alt="Clarity Over Process" className="w-50 h-50 object-contain" />,
  },
  {
    title: "Systems Built to Evolve",
    description: "Future-ready architectures that scale with your business growth and changing needs.",
    icon: <img src="/icons/system.gif" alt="Systems Built to Evolve" className="w-50 h-50 object-contain" />,
  },
  {
    title: "End-to-End Ownership",
    description: "Complete responsibility from ideation to deployment and ongoing maintenance.",
    icon: <img src="/icons/End-to-End%20Ownership.png" alt="End-to-End Ownership" className="w-50 h-50 object-contain" />,
  },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  
  return (
    <>
      {/* Company Overview */}
      <Section
        title="About Ennobridge"
        subtitle="ENNOBRIDGE TECH "
        centered
        background="gradient"
      >
        <motion.div 
          className="max-w-4xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12"
            variants={staggerContainer}
          >
            <motion.div className="text-center" variants={fadeUp}>
              <div className="text-2xl font-accent font-bold text-primary mb-2">2022</div>
              <div className="text-textSecondary">Incorporated</div>
            </motion.div>
            <motion.div className="text-center" variants={fadeUp}>
              <div className="text-2xl font-accent font-bold text-primary mb-2">India</div>
              <div className="text-textSecondary">Headquarters</div>
            </motion.div>
            <motion.div className="text-center" variants={fadeUp}>
              <div className="text-2xl font-accent font-bold text-primary mb-2">Global</div>
              <div className="text-textSecondary">Operations</div>
            </motion.div>
          </motion.div>

          <motion.p 
            className="text-lg md:text-xl text-textSecondary text-center leading-relaxed"
            variants={fadeUp}
          >
            Founded in 2022, Ennobridge Tech is a software development company that specializes 
            in building and scaling systems that power long-term growth. We combine residential 
            engineering teams, expert consultant networks, and AI-augmented workflows to deliver 
            exceptional results for our clients across diverse industries.
          </motion.p>
        </motion.div>
      </Section>

      {/* Philosophy & Vision */}
      <Section
        title="Our Philosophy"
        subtitle="Building lasting partnerships through clarity, expertise, and evolving systems"
        centered
        background="surface"
      >
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={staggerContainer}
        >
          <motion.div className="text-center" variants={fadeUp}>
            <div className="w-40 h-40 mx-auto mb-4 flex items-center justify-center">
              <img src="/icons/Hand-in-hand cooperation.gif" alt="Partner-Led Delivery" className="w-full h-full object-contain" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-textPrimary mb-3">
              Partner-Led Delivery
            </h3>
            <p className="text-textSecondary">
              We believe in building long-term partnerships with direct access to senior 
              technical leaders throughout your project journey.
            </p>
          </motion.div>

          <motion.div className="text-center" variants={fadeUp}>
            <div className="w-40 h-40 mx-auto mb-4 flex items-center justify-center">
              <img src="/icons/Clarity%20Over%20Process.png" alt="Clarity Over Process" className="w-full h-full object-contain" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-textPrimary mb-3">
              Clarity Over Process
            </h3>
            <p className="text-textSecondary">
              Transparent communication and clear deliverables without the bureaucratic 
              overhead that slows down innovation.
            </p>
          </motion.div>

          <motion.div className="text-center" variants={fadeUp}>
            <div className="w-40 h-40 mx-auto mb-4 flex items-center justify-center">
              <img src="/icons/system.gif" alt="Systems Built to Evolve" className="w-full h-full object-contain" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-textPrimary mb-3">
              Systems Built to Evolve
            </h3>
            <p className="text-textSecondary">
              Future-ready architectures designed to scale and adapt with your 
              business growth and changing market demands.
            </p>
          </motion.div>
        </motion.div>
      </Section>

      {/* Delivery Model */}
      <Section
        title="Our Delivery Model"
        subtitle="Unique approach that combines human expertise with intelligent automation"
        centered
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {deliveryModel.map((model, index) => (
            <USPCard
              key={index}
              title={model.title}
              description={model.description}
              image={model.image}
            />
          ))}
        </div>
      </Section>

      {/* Why Clients Choose Ennobridge */}
      <Section
        title="Why Clients Choose Ennobridge"
        subtitle="Distinctive advantages that set us apart in the software development landscape"
        centered
        background="surface"
      >
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={staggerContainer}
        >
          {whyChooseUs.map((reason, index) => (
            <motion.div key={index} variants={fadeUp}>
              <Card
                title={reason.title}
                description={reason.description}
                icon={reason.icon}
                compact
              />
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* Call to Action */}
      <Section centered>
        <motion.div 
          className="text-center max-w-3xl mx-auto"
          variants={fadeUp}
        >
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-textPrimary mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-lg text-textSecondary mb-8">
            Let's discuss how our unique delivery model can accelerate your software development 
            goals and drive sustainable growth for your business.
          </p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            variants={fadeUp}
          >
            <button
              className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg shadow-lg hover:shadow-glow transition-all duration-normal"
              onClick={() => setIsChatbotOpen(true)}
            >
              Start a Conversation
            </button>
            <button
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg transition-all duration-normal"
              onClick={() => {
                navigate('/services');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              View Our Services
            </button>
          </motion.div>
        </motion.div>
      </Section>
      
      {/* Chatbot Modal */}
      <ChatbotModal 
        isOpen={isChatbotOpen} 
        onClose={() => setIsChatbotOpen(false)} 
      />
    </>
  );
}